import { BellOff, Calendar, Clock, Edit, MessageSquare, Phone, Send, Trash2 } from 'lucide-react';
import type { Cita } from '../../types';
import { enviarSMS, enviarWhatsApp } from '../../lib/messaging';

interface Props {
  cita: Cita;
  onEditar: (cita: Cita) => void;
  onEliminar: (id: string) => void;
  onSilenciar: (cita: Cita) => void;
}

export function TarjetaCita({ cita, onEditar, onEliminar, onSilenciar }: Props) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-xl font-bold text-gray-800">{cita.cliente}</h3>
            {cita.notificacionesSilenciadas && (
              <span className="bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <BellOff size={12} />
                Silenciado
              </span>
            )}
          </div>
          <div className="space-y-2 text-gray-600">
            <p className="flex items-center gap-2">
              <Calendar size={18} className="text-indigo-600" />
              {new Date(cita.fecha + 'T00:00:00').toLocaleDateString('es-MX', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
            <p className="flex items-center gap-2">
              <Clock size={18} className="text-indigo-600" />
              {cita.hora}
            </p>
            {cita.telefono && (
              <p className="flex items-center gap-2">
                <Phone size={18} className="text-indigo-600" />
                {cita.telefono}
              </p>
            )}
            {cita.notas && <p className="text-sm text-gray-500 mt-2 italic">{cita.notas}</p>}
          </div>

          <div className="flex gap-2 mt-4 flex-wrap">
            {cita.telefono && (
              <>
                <button
                  onClick={() => enviarWhatsApp(cita)}
                  className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors"
                >
                  <MessageSquare size={16} />
                  WhatsApp
                </button>
                <button
                  onClick={() => enviarSMS(cita)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors"
                >
                  <Send size={16} />
                  SMS
                </button>
              </>
            )}
            {!cita.notificacionesSilenciadas && (
              <button
                onClick={() => onSilenciar(cita)}
                className="bg-orange-100 hover:bg-orange-200 text-orange-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors"
              >
                <BellOff size={16} />
                Silenciar
              </button>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEditar(cita);
            }}
            className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
            title="Editar/Reagendar"
          >
            <Edit size={20} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEliminar(cita.id);
            }}
            className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
            title="Eliminar"
          >
            <Trash2 size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}

interface TarjetaCitaPasadaProps {
  cita: Cita;
  onEliminar: (id: string) => void;
}

export function TarjetaCitaPasada({ cita, onEliminar }: TarjetaCitaPasadaProps) {
  return (
    <div className="bg-gray-50 rounded-xl shadow p-6 opacity-60">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-gray-700 mb-2">{cita.cliente}</h3>
          <div className="space-y-1 text-gray-600 text-sm">
            <p className="flex items-center gap-2">
              <Calendar size={16} />
              {new Date(cita.fecha + 'T00:00:00').toLocaleDateString('es-MX')}
            </p>
            <p className="flex items-center gap-2">
              <Clock size={16} />
              {cita.hora}
            </p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onEliminar(cita.id);
          }}
          className="text-gray-400 hover:text-red-500 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 size={18} />
        </button>
      </div>
    </div>
  );
}
